import React from "react";
import { motion } from "framer-motion";
import { Cpu, Smartphone, ShieldCheck, FileCheck } from "lucide-react";

const points = [
  {
    icon: Cpu,
    title: "Compact &amp; au point de soin",
    desc: "Un instrument de paillasse silencieux conçu pour s'intégrer dans tout type d'environnement clinique.",
  },
  {
    icon: Smartphone,
    title: "Interface intuitive",
    desc: "Écran tactile guidant l'utilisateur étape par étape — formation rapide des équipes soignantes.",
  },
  {
    icon: FileCheck,
    title: "Cartouche MeMed BV®",
    desc: "Sérum capillaire ou veineux — une cartouche à usage unique, un code-barres traçable.",
  },
  {
    icon: ShieldCheck,
    title: "Sécurité &amp; conformité",
    desc: "Conforme aux standards de biologie médicale et compatible avec les workflows hospitaliers.",
  },
];

const Product = () => {
  return (
    <section
      id="product"
      data-testid="product-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 -left-40 w-[420px] h-[420px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(91,196,209,0.25), transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-6 order-2 lg:order-1"
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium mb-4">
            Le dispositif
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight">
            MeMed Key®,{" "}
            <span className="font-semibold">
              une plateforme conçue pour le terrain.
            </span>
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-xl">
            Pensé pour les services d&apos;urgences, les centres de soins non
            programmés et les laboratoires de biologie médicale, MeMed Key®
            apporte un résultat fiable directement au point de soin —
            sans expédition d&apos;échantillons, sans attente.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {points.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  data-testid={`product-point-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="bg-[#0E1530] border border-white/10 rounded-2xl p-5 hover:border-[#5BC4D1]/30 transition-colors"
                >
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#5BC4D1]/10 border border-[#5BC4D1]/20 text-[#5BC4D1] mb-3">
                    <Icon size={16} strokeWidth={1.7} />
                  </div>
                  <div
                    className="font-medium text-white text-[15px]"
                    dangerouslySetInnerHTML={{ __html: p.title }}
                  />
                  <p
                    className="text-xs text-slate-400 mt-2 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: p.desc }}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-6 order-1 lg:order-2"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative">
            <motion.img
              src="https://customer-assets.emergentagent.com/job_nexo-branding/artifacts/6h5t7ahc_maxresdefault.jpg"
              alt="MeMed Key® dans son environnement de laboratoire avec pipette et sac biohazard"
              className="w-full h-auto rounded-3xl border border-white/10 shadow-2xl"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Floating cartridge image */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_nexo-branding/artifacts/jlxc1yc0_images.jpeg"
              alt="Cartouche MeMed BV®"
              className="absolute -bottom-8 -left-8 w-44 md:w-56 rounded-2xl border border-white/15 shadow-2xl bg-white"
              initial={{ opacity: 0, y: 20, rotate: -8 }}
              whileInView={{ opacity: 1, y: 0, rotate: -8 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.9 }}
            />
            <div
              className="absolute -top-6 -right-6 px-4 py-3 rounded-2xl bg-[#0A1128]/90 backdrop-blur-xl border border-[#5BC4D1]/30 shadow-xl"
              data-testid="product-callout"
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                Plateforme
              </div>
              <div className="font-display text-base font-medium text-[#5BC4D1] mt-0.5">
                MeMed Key®
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Product;
